// Shared publication policy for database routes, SEO, and client discovery.
// Legacy records remain published until an explicit editorial decision is made.
const USE_CASE_PUBLICATION_STATES = ['published', 'draft', 'held', 'merge-pending', 'redirect', 'retired'];
const slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function publicationState(row) {
  return row?.publication?.status ?? 'published';
}

function isUseCaseAvailable(row) {
  return !!row && !row.deletedAt && row.flags?.isEnabled !== false
    && USE_CASE_PUBLICATION_STATES.includes(publicationState(row))
    && !['draft', 'retired'].includes(publicationState(row));
}

function isUseCaseSeoIndexable(row) {
  return isUseCaseAvailable(row) && row.flags?.isSeoIndexable !== false
    && publicationState(row) !== 'redirect';
}

function isUseCaseDiscoverable(row) {
  return isUseCaseSeoIndexable(row) && row.flags?.isDiscoverable !== false
    && publicationState(row) === 'published';
}

function useCasePublicationFilter({ discovery = false, seo = false } = {}) {
  const filter = {
    deletedAt: null,
    'flags.isEnabled': { $ne: false },
    'publication.status': { $in: discovery
      ? ['published', null]
      : seo ? ['published', 'held', 'merge-pending', null]
        : ['published', 'held', 'merge-pending', 'redirect', null] },
  };
  if (seo || discovery) filter['flags.isSeoIndexable'] = { $ne: false };
  if (discovery) filter['flags.isDiscoverable'] = { $ne: false };
  return filter;
}

function isUseCasePublication(value, ownSlug) {
  if (value == null) return true;
  if (!USE_CASE_PUBLICATION_STATES.includes(value.status)) return false;
  if (value.destinationSlug && (!slugPattern.test(value.destinationSlug) || value.destinationSlug === ownSlug)) return false;
  if (value.status === 'redirect') {
    const review = value.review || {};
    if (!value.destinationSlug || review.trafficReviewed !== true || review.backlinksReviewed !== true || review.replacementReady !== true) return false;
  }
  return true;
}

function useCaseRedirect(row) {
  return publicationState(row) === 'redirect' && isUseCasePublication(row.publication, row.slug)
    ? `/use-cases/${row.publication.destinationSlug}` : null;
}


// Hub/menu records deliberately omit prompts, source files and demonstration
// packages. Detail responses retain the full existing public contract.
const USE_CASE_SUMMARY_FIELDS = 'slug name description icon eyebrow headline subtitle order hubCategory flags publication';

function useCaseSummary(row) {
  return {
    id: row.slug,
    slug: row.slug,
    name: row.name,
    description: row.description || '',
    icon: row.icon || 'sparkles',
    eyebrow: row.eyebrow || '',
    headline: row.headline || '',
    subtitle: row.subtitle || '',
    order: typeof row.order === 'number' ? row.order : 999,
    hubCategory: row.hubCategory || 'business',
    flags: {
      isEnabled: row.flags?.isEnabled !== false, isFeatured: !!row.flags?.isFeatured,
      isSeoIndexable: row.flags?.isSeoIndexable !== false,
      isDiscoverable: row.flags?.isDiscoverable !== false,
    },
    publication: { status: row.publication?.status || 'published' },
  };
}


// Safe inline JSON transport; DOM lifecycle stays in the client.
const INITIAL_DATA_ID = 'use-case-initial-data';

function serializeUseCaseInitialData(payload) {
  return JSON.stringify(payload).replace(/[<>&\u2028\u2029]/g, character =>
    `\\u${character.charCodeAt(0).toString(16).padStart(4, '0')}`);
}

module.exports = {
  USE_CASE_PUBLICATION_STATES, publicationState, isUseCaseAvailable,
  isUseCaseSeoIndexable, isUseCaseDiscoverable, useCasePublicationFilter,
  isUseCasePublication, useCaseRedirect, USE_CASE_SUMMARY_FIELDS, useCaseSummary,
  INITIAL_DATA_ID, serializeUseCaseInitialData,
};
